<template>
  <Teleport to="body">
    <Transition name="viewer-fade">
      <div v-if="visible" class="image-viewer-overlay" @keydown="handleKeydown" tabindex="-1" ref="overlayRef">
        <!-- Top bar -->
        <div class="viewer-topbar">
          <div class="flex align-items-center gap-3">
            <span class="text-white font-medium" style="max-width: 400px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap">
              {{ currentImage?.name }}
            </span>
          </div>
          <div class="flex align-items-center gap-3">
            <span class="text-gray-400 text-sm">{{ currentIndex + 1 }} / {{ images.length }}</span>
            <button class="viewer-btn" @click="close" v-tooltip.bottom="'Закрыть (Esc)'">
              <i class="pi pi-times"></i>
            </button>
          </div>
        </div>

        <!-- Main area -->
        <div class="viewer-main" @wheel.prevent="handleWheel"
          @mousedown="startPan" @mousemove="onPan" @mouseup="endPan" @mouseleave="endPan">
          <!-- Left nav -->
          <button class="viewer-nav viewer-nav-left" @click="navigate(-1)" :disabled="currentIndex <= 0">
            <i class="pi pi-chevron-left text-2xl"></i>
          </button>

          <!-- Image -->
          <img v-if="currentImage"
            :src="currentImage.url"
            :alt="currentImage.name"
            :style="imageTransformStyle"
            class="viewer-image"
            @load="onImageLoad"
            draggable="false"
          />

          <!-- Right nav -->
          <button class="viewer-nav viewer-nav-right" @click="navigate(1)" :disabled="currentIndex >= images.length - 1">
            <i class="pi pi-chevron-right text-2xl"></i>
          </button>
        </div>

        <!-- Bottom bar -->
        <div class="viewer-bottombar">
          <div class="flex align-items-center gap-3">
            <button class="viewer-btn" @click="zoomOut" v-tooltip.top="'Уменьшить (-)'"><i class="pi pi-minus"></i></button>
            <span class="text-white text-sm" style="min-width: 3rem; text-align: center">{{ zoomPercent }}%</span>
            <button class="viewer-btn" @click="zoomIn" v-tooltip.top="'Увеличить (+)'"><i class="pi pi-plus"></i></button>
            <button class="viewer-btn" @click="resetZoom" v-tooltip.top="'Сбросить (0)'"><i class="pi pi-refresh"></i></button>
          </div>
          <div class="flex align-items-center gap-3 text-gray-400 text-sm">
            <span v-if="currentImage">{{ formatFileSize(currentImage.size) }}</span>
            <span v-if="imageResolution">{{ imageResolution }}</span>
          </div>
          <div class="flex align-items-center gap-2">
            <button class="viewer-btn" @click="$emit('download', currentImage)" v-tooltip.top="'Скачать'"><i class="pi pi-download"></i></button>
            <button class="viewer-btn" @click="$emit('rename', currentImage)" v-tooltip.top="'Переименовать'"><i class="pi pi-pencil"></i></button>
            <button class="viewer-btn viewer-btn-danger" @click="$emit('delete', currentImage)" v-tooltip.top="'Удалить'"><i class="pi pi-trash"></i></button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import Tooltip from 'primevue/tooltip'
import { formatFileSize } from '../utils/dateHelpers'

const vTooltip = Tooltip

const props = defineProps({
  visible: Boolean,
  images: { type: Array, default: () => [] },
  initialIndex: { type: Number, default: 0 }
})

const emit = defineEmits(['update:visible', 'delete', 'rename', 'download'])

const overlayRef = ref(null)
const currentIndex = ref(0)
const zoom = ref(1)
const panX = ref(0)
const panY = ref(0)
const isPanning = ref(false)
const panStart = ref({ x: 0, y: 0 })
const imageResolution = ref('')

const currentImage = computed(() => props.images[currentIndex.value] || null)
const zoomPercent = computed(() => Math.round(zoom.value * 100))

const imageTransformStyle = computed(() => ({
  transform: `scale(${zoom.value}) translate(${panX.value}px, ${panY.value}px)`,
  cursor: zoom.value > 1 ? (isPanning.value ? 'grabbing' : 'grab') : 'default',
  transition: isPanning.value ? 'none' : 'transform 0.15s ease'
}))

// Watch visible to set initial index and focus
watch(() => props.visible, (val) => {
  if (val) {
    currentIndex.value = props.initialIndex
    resetZoom()
    nextTick(() => overlayRef.value?.focus())
  }
})

// Reset zoom on image change
watch(currentIndex, () => {
  resetZoom()
  imageResolution.value = ''
})

function close() {
  emit('update:visible', false)
}

function navigate(dir) {
  const newIdx = currentIndex.value + dir
  if (newIdx >= 0 && newIdx < props.images.length) {
    currentIndex.value = newIdx
  }
}

function zoomIn() {
  zoom.value = Math.min(5, zoom.value + 0.25)
}

function zoomOut() {
  zoom.value = Math.max(0.1, zoom.value - 0.25)
}

function resetZoom() {
  zoom.value = 1
  panX.value = 0
  panY.value = 0
}

function handleWheel(e) {
  const delta = e.deltaY > 0 ? -0.1 : 0.1
  zoom.value = Math.max(0.1, Math.min(5, zoom.value + delta))
  if (zoom.value <= 1) {
    panX.value = 0
    panY.value = 0
  }
}

function startPan(e) {
  if (zoom.value <= 1) return
  isPanning.value = true
  panStart.value = {
    x: e.clientX - panX.value * zoom.value,
    y: e.clientY - panY.value * zoom.value
  }
}

function onPan(e) {
  if (!isPanning.value) return
  panX.value = (e.clientX - panStart.value.x) / zoom.value
  panY.value = (e.clientY - panStart.value.y) / zoom.value
}

function endPan() {
  isPanning.value = false
}

function onImageLoad(e) {
  const img = e.target
  if (img.naturalWidth && img.naturalHeight) {
    imageResolution.value = `${img.naturalWidth} \u00d7 ${img.naturalHeight}`
  }
}

function handleKeydown(e) {
  switch (e.key) {
    case 'ArrowLeft':
      e.preventDefault()
      navigate(-1)
      break
    case 'ArrowRight':
      e.preventDefault()
      navigate(1)
      break
    case 'Escape':
      e.preventDefault()
      close()
      break
    case '+':
    case '=':
      e.preventDefault()
      zoomIn()
      break
    case '-':
      e.preventDefault()
      zoomOut()
      break
    case '0':
      e.preventDefault()
      resetZoom()
      break
  }
}

</script>

<style scoped>
.image-viewer-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  flex-direction: column;
  outline: none;
}

.viewer-topbar, .viewer-bottombar {
  flex-shrink: 0;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(0, 0, 0, 0.5);
}

.viewer-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  min-height: 0;
}

.viewer-image {
  max-width: 90%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  will-change: transform;
}

.viewer-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  z-index: 1;
}
.viewer-nav:hover:not(:disabled) { background: rgba(255, 255, 255, 0.25); }
.viewer-nav:disabled { opacity: 0.3; cursor: default; }
.viewer-nav-left { left: 1rem; }
.viewer-nav-right { right: 1rem; }

.viewer-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}
.viewer-btn:hover { background: rgba(255, 255, 255, 0.25); }
.viewer-btn-danger:hover { background: rgba(239, 68, 68, 0.5); }

.viewer-fade-enter-active, .viewer-fade-leave-active {
  transition: opacity 0.2s ease;
}
.viewer-fade-enter-from, .viewer-fade-leave-to {
  opacity: 0;
}
</style>
